import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { finalize } from 'rxjs';
import { LoaderService } from '../../ui/core/loader.service';

/**
 * Mutating methods that should trigger the global loading spinner.
 * GET requests are intentionally excluded — those keep their existing
 * per-component spinners.
 */
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Background housekeeping the user never asked for. The overlay is a
 * full-screen blocking backdrop, so it must only appear for a mutation the
 * vendor actually triggered.
 *
 * BLK-37 added an FCM token registration that fires on login and on token
 * rotation. Without this exemption a dark backdrop flashed over the /bookings
 * redirect right after signing in, and again at any mid-session rotation, for a
 * request the vendor has no idea exists.
 */
/**
 * WI-V-02 added the second entry. `POST /bookings/quote` is a READ that happens
 * to be a POST — it creates nothing and holds nothing — and the walk-in form
 * fires it on every relevant change with a 300ms debounce. Without the
 * exemption the full-screen blocking backdrop strobed over the form while the
 * vendor was still picking dates, and the one mutation it exists for (the
 * create) became indistinguishable from typing.
 */
const SILENT_MUTATIONS = ['/auth/office/fcm-token', '/bookings/quote'];

/**
 * The ref-count moved INTO `LoaderService` (it counts `show()`/`hide()` pairs
 * and only clears at zero), so the module-level `let inFlight` that used to sit
 * here is gone. Behaviour is identical — one overlay for N concurrent
 * mutations, hidden only when the last finishes — and a component that wants to
 * block the screen for a non-HTTP task can now join the same count instead of
 * racing this interceptor.
 *
 * The spinner itself changed from `<ngx-spinner>` to `<ui-busy-overlay>`; the
 * TRIGGER — the method set and the exemption list above — is untouched.
 */
export const loadingInterceptor: HttpInterceptorFn = (req, next) => {
  if (!MUTATING_METHODS.has(req.method.toUpperCase())) {
    return next(req);
  }

  const url = req.url.toLowerCase();
  if (SILENT_MUTATIONS.some((path) => url.includes(path))) {
    return next(req);
  }

  const loader = inject(LoaderService);

  loader.show();

  return next(req).pipe(finalize(() => loader.hide()));
};
